import React from 'react';
import { toast } from 'react-toastify';
import './ErrorBoundary.css';

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const isChunkError = error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk');

    if (isChunkError) {
      console.group('ChunkErrorBoundary caught chunk loading error');
      console.error('Error:', error);
      console.groupEnd();
      toast.error("Slow internet connection detected. Some parts of the site may not load properly. Please check your internet.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
    } else {
      console.error('ChunkErrorBoundary caught a runtime/render error:', error);
      console.error('Component stack:', errorInfo?.componentStack);
      toast.error("Something went wrong while loading this page. Please refresh and try again.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = this.state.error && (
        this.state.error.name === 'ChunkLoadError' ||
        this.state.error.message?.includes('Loading chunk')
      );

      if (isChunkError && this.state.retryCount < 2) {
        setTimeout(() => {
          this.handleRetry();
        }, 1500 + (this.state.retryCount * 1000));

        return this.props.fallback || <div className="chunk-loading-fallback">Loading resources...</div>;
      }

      return this.props.fallback || (
        <div className="chunk-loading-fallback" style={{ minHeight: '40vh', display: 'grid', placeItems: 'center', textAlign: 'center', padding: '24px' }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>Something went wrong</div>
            <div style={{ opacity: 0.75, marginBottom: '16px' }}>Please refresh the page to continue.</div>
            <button type="button" onClick={this.handleReload} className="btn theme-button-bg-color theme-text-color">Refresh</button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
